import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Question, AnswerValue, normalizeType } from '../types/question';

interface QuestionRendererProps {
  question: Question;
  answer: AnswerValue;
  isRTL: boolean;
  isChecked: boolean;
  mode: 'exam' | 'training';
  disabled?: boolean;
  onAnswerChange: (value: AnswerValue) => void;
}

export default function QuestionRenderer({
  question,
  answer,
  isRTL,
  isChecked,
  mode,
  disabled,
  onAnswerChange,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const type = normalizeType(question.type);
  const options = isRTL
    ? (question.options_ar?.length ? question.options_ar : question.options_en)
    : question.options_en;

  // Common helpers for letter labels
  const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

  const handleOptionClick = (opt: string) => {
    if (disabled || (isChecked && mode === 'training')) return;
    if (type === 'multiple') {
      const current = Array.isArray(answer) ? answer : [];
      const next = current.includes(opt)
        ? current.filter((item) => item !== opt)
        : [...current, opt];
      onAnswerChange(next);
    } else if (type === 'single' || type === 'hotspot' || type === 'truefalse') {
      onAnswerChange(opt);
    }
  };

  // Single / hotspot / truefalse are all single-select radio-style
  if (type === 'single' || type === 'hotspot' || type === 'truefalse') {
    return (
      <div className="space-y-3">
        {options?.map((opt: string, idx: number) => {
          const selected = answer === opt;
          const correct = isCorrectOption(question, opt);
          let optionClass = 'w-full text-left p-4 border rounded-lg transition-all flex items-start gap-3 ';
          if (isChecked) {
            if (correct) optionClass += 'border-green-500 bg-green-50/50';
            else if (selected) optionClass += 'border-red-500 bg-red-50/50';
            else optionClass += 'border-border opacity-60';
          } else if (selected) {
            optionClass += 'border-primary bg-primary/5';
          } else {
            optionClass += 'border-border hover:bg-accent';
          }
          return (
            <div key={idx} className="space-y-1">
              <button
                onClick={() => handleOptionClick(opt)}
                disabled={disabled || (isChecked && mode === 'training')}
                className={optionClass}
              >
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-primary' : 'border-gray-300'}`}>
                  {selected && <span className="w-2.5 h-2.5 rounded-full bg-primary" />}
                </span>
                <span className="font-medium text-muted-foreground flex-shrink-0">{letters[idx] || idx + 1}.</span>
                <span>{opt}</span>
              </button>
              {mode === 'training' && isChecked && (
                <div className={`text-xs px-4 py-2 rounded-lg ${correct ? 'bg-green-50 text-green-800' : selected ? 'bg-red-50 text-red-800' : 'bg-muted/40 text-muted-foreground'}`}>
                  <span className="font-medium">{correct ? t('exam.correctLabel') : t('exam.whyNot')}</span>
                  {isRTL
                    ? question.choice_explanations_ar?.[idx] || question.choice_explanations_en?.[idx] || ''
                    : question.choice_explanations_en?.[idx] || question.choice_explanations_ar?.[idx] || ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Multiple response
  if (type === 'multiple') {
    return (
      <div className="space-y-3">
        {options?.map((opt: string, idx: number) => {
          const selected = Array.isArray(answer) && answer.includes(opt);
          const correct = isCorrectOption(question, opt);
          let optionClass = 'w-full text-left p-4 border rounded-lg transition-all flex items-start gap-3 ';
          if (isChecked) {
            if (correct) optionClass += 'border-green-500 bg-green-50/50';
            else if (selected) optionClass += 'border-red-500 bg-red-50/50';
            else optionClass += 'border-border opacity-60';
          } else if (selected) {
            optionClass += 'border-primary bg-primary/5';
          } else {
            optionClass += 'border-border hover:bg-accent';
          }
          return (
            <div key={idx} className="space-y-1">
              <button
                onClick={() => handleOptionClick(opt)}
                disabled={disabled || (isChecked && mode === 'training')}
                className={optionClass}
              >
                <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                  {selected && <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </span>
                <span className="font-medium text-muted-foreground flex-shrink-0">{letters[idx] || idx + 1}.</span>
                <span>{opt}</span>
              </button>
              {mode === 'training' && isChecked && (
                <div className={`text-xs px-4 py-2 rounded-lg ${correct ? 'bg-green-50 text-green-800' : selected ? 'bg-red-50 text-red-800' : 'bg-muted/40 text-muted-foreground'}`}>
                  <span className="font-medium">{correct ? t('exam.correctLabel') : t('exam.whyNot')}</span>
                  {isRTL
                    ? question.choice_explanations_ar?.[idx] || question.choice_explanations_en?.[idx] || ''
                    : question.choice_explanations_en?.[idx] || question.choice_explanations_ar?.[idx] || ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Matching: match each item in column A to an item in column B
  if (type === 'matching') {
    const itemsA = isRTL
      ? (question.match_items_a_ar?.length ? question.match_items_a_ar : question.match_items_a_en)
      : question.match_items_a_en;
    const itemsB = isRTL
      ? (question.match_items_b_ar?.length ? question.match_items_b_ar : question.match_items_b_en)
      : question.match_items_b_en;
    const currentMapping = (answer as unknown as Record<string, string>) || {};
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground mb-2">{t('exam.matchPrompt')}</p>
        {itemsA?.map((itemA, idx) => {
          const selectedB = currentMapping[itemA] || '';
          const correctB = getCorrectMatch(question, itemA);
          const isCorrectMatch = isChecked && selectedB === correctB;
          const isWrongMatch = isChecked && selectedB && selectedB !== correctB;
          return (
            <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border rounded-lg">
              <span className="font-medium sm:w-1/2">{itemA}</span>
              <select
                value={selectedB}
                onChange={(e) => {
                  if (disabled || (isChecked && mode === 'training')) return;
                  onAnswerChange({ ...currentMapping, [itemA]: e.target.value } as unknown as AnswerValue);
                }}
                disabled={disabled || (isChecked && mode === 'training')}
                className={`flex-1 p-2 border rounded-lg ${isCorrectMatch ? 'border-green-500 bg-green-50/50' : isWrongMatch ? 'border-red-500 bg-red-50/50' : 'border-border'}`}
              >
                <option value="">{t('exam.selectMatch')}</option>
                {itemsB?.map((itemB, bIdx) => (
                  <option key={bIdx} value={itemB}>{itemB}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    );
  }

  // Sequence / ordering: reorder items
  if (type === 'sequence') {
    const items = isRTL
      ? (question.sequence_items_ar?.length ? question.sequence_items_ar : question.sequence_items_en)
      : question.sequence_items_en;
    const order = Array.isArray(answer) && answer.length ? answer : (items || []);
    const [localItems, setLocalItems] = useState<string[]>(order);
    const updateOrder = (newOrder: string[]) => {
      setLocalItems(newOrder);
      onAnswerChange(newOrder);
    };
    const moveItem = (index: number, direction: 'up' | 'down') => {
      if (disabled || (isChecked && mode === 'training')) return;
      const newOrder = [...localItems];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= newOrder.length) return;
      [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
      updateOrder(newOrder);
    };
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground mb-2">{t('exam.sequencePrompt')}</p>
        {localItems?.map((item, idx) => {
          const correctOrder = getCorrectSequence(question);
          const isCorrect = isChecked && correctOrder[idx] === item;
          const isWrong = isChecked && correctOrder[idx] !== item;
          return (
            <div key={idx} className={`flex items-center gap-3 p-3 border rounded-lg ${isCorrect ? 'border-green-500 bg-green-50/50' : isWrong ? 'border-red-500 bg-red-50/50' : 'border-border'}`}>
              <span className="font-bold text-muted-foreground w-8">{idx + 1}.</span>
              <span className="flex-1">{item}</span>
              <button
                onClick={() => moveItem(idx, 'up')}
                disabled={idx === 0 || disabled || (isChecked && mode === 'training')}
                className="p-2 border rounded hover:bg-accent disabled:opacity-50"
              >
                ↑
              </button>
              <button
                onClick={() => moveItem(idx, 'down')}
                disabled={idx === localItems.length - 1 || disabled || (isChecked && mode === 'training')}
                className="p-2 border rounded hover:bg-accent disabled:opacity-50"
              >
                ↓
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  // Drag-and-drop / categorization: assign each item to a category
  if (type === 'dragdrop') {
    const categories = isRTL
      ? (question.categories_ar?.length ? question.categories_ar : question.categories_en)
      : question.categories_en;
    const items = isRTL
      ? (question.dragdrop_items_ar?.length ? question.dragdrop_items_ar : question.dragdrop_items_en)
      : question.dragdrop_items_en;
    const currentMapping = (answer as unknown as Record<string, string[]>) || {};
    const itemAssignments: Record<string, string> = {};
    Object.entries(currentMapping).forEach(([cat, catItems]) => {
      catItems.forEach((item) => { itemAssignments[item] = cat; });
    });
    const assignItem = (item: string, category: string) => {
      if (disabled || (isChecked && mode === 'training')) return;
      const newAssignments = { ...itemAssignments, [item]: category };
      const newMapping: Record<string, string[]> = {};
      categories?.forEach((cat) => { newMapping[cat] = []; });
      Object.entries(newAssignments).forEach(([it, cat]) => {
        if (cat) {
          if (!newMapping[cat]) newMapping[cat] = [];
          newMapping[cat].push(it);
        }
      });
      onAnswerChange(newMapping as unknown as AnswerValue);
    };
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground mb-2">{t('exam.dragdropPrompt')}</p>
        {items?.map((item, idx) => {
          const correctCat = getCorrectCategory(question, item);
          const selectedCat = itemAssignments[item] || '';
          const isCorrect = isChecked && selectedCat === correctCat;
          const isWrong = isChecked && selectedCat && selectedCat !== correctCat;
          return (
            <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border rounded-lg">
              <span className="font-medium sm:w-1/2">{item}</span>
              <select
                value={selectedCat}
                onChange={(e) => assignItem(item, e.target.value)}
                disabled={disabled || (isChecked && mode === 'training')}
                className={`flex-1 p-2 border rounded-lg ${isCorrect ? 'border-green-500 bg-green-50/50' : isWrong ? 'border-red-500 bg-red-50/50' : 'border-border'}`}
              >
                <option value="">{t('exam.selectCategory')}</option>
                {categories?.map((cat, cIdx) => (
                  <option key={cIdx} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    );
  }

  // Calculation: numeric input
  if (type === 'calculation') {
    const numericAnswer = answer === null || answer === undefined ? '' : String(answer);
    const correctValue = Number(question.correct_answer);
    const tolerance = question.calculation_tolerance ?? 0.01;
    const isCorrect = isChecked && Math.abs(Number(answer) - correctValue) <= tolerance;
    const isWrong = isChecked && !isCorrect && answer !== '';
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground mb-2">{t('exam.calculationPrompt')}</p>
        <input
          type="number"
          step="any"
          value={numericAnswer}
          onChange={(e) => onAnswerChange(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={disabled || (isChecked && mode === 'training')}
          className={`w-full p-4 border rounded-lg text-lg ${isCorrect ? 'border-green-500 bg-green-50/50' : isWrong ? 'border-red-500 bg-red-50/50' : 'border-border'}`}
          placeholder={t('exam.enterValue')}
        />
        {isChecked && (
          <div className={`text-sm p-3 rounded-lg ${isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {isCorrect ? t('exam.correctLabel') : `${t('exam.expectedValue')}: ${correctValue}`}
          </div>
        )}
      </div>
    );
  }

  return null;
}

// Helper functions used by the renderer and by ExamPage
function getOptionIndex(question: Question, answer: string): number {
  if (answer.length === 1 && answer >= 'A' && answer <= 'Z') {
    return answer.charCodeAt(0) - 'A'.charCodeAt(0);
  }
  const englishIndex = question.options_en?.indexOf(answer);
  if (englishIndex !== undefined && englishIndex >= 0) return englishIndex;
  return question.options_ar?.indexOf(answer) ?? -1;
}

export function getCorrectIndexes(question: Question): number[] {
  let correctAnswers: string[];
  if (Array.isArray(question.correct_answer)) {
    correctAnswers = question.correct_answer;
  } else if (typeof question.correct_answer === 'string' && question.correct_answer.includes('|')) {
    correctAnswers = question.correct_answer.split('|');
  } else if (typeof question.correct_answer === 'string') {
    correctAnswers = [question.correct_answer];
  } else {
    return [];
  }
  return correctAnswers.map((ans) => getOptionIndex(question, ans)).filter((idx) => idx >= 0);
}

export function formatCorrectAnswer(question: Question): string {
  const correct = question.correct_answer;
  const type = normalizeType(question.type);
  if (correct === null || correct === undefined) return '';
  if (type === 'matching') {
    const expected = parseMatchingCorrect(correct);
    return Object.entries(expected).map(([a, b]) => `${a} → ${b}`).join(', ');
  }
  if (type === 'sequence') {
    return getCorrectSequence(question).join(' → ');
  }
  if (type === 'dragdrop') {
    const expected = parseDragDropCorrect(correct);
    return Object.entries(expected).map(([cat, items]) => `${cat}: ${items.join(', ')}`).join(' | ');
  }
  if (type === 'calculation') {
    return String(correct);
  }
  if (Array.isArray(correct)) {
    return correct.join(', ');
  }
  return String(correct);
}

export function getQuestionTypeLabel(type: string | undefined, t: (key: string) => string): string {
  const normalized = normalizeType(type);
  switch (normalized) {
    case 'single': return t('exam.singleChoice');
    case 'multiple': return t('exam.multipleResponse');
    case 'matching': return t('exam.matching');
    case 'sequence': return t('exam.sequence');
    case 'hotspot': return t('exam.hotspot');
    case 'dragdrop': return t('exam.dragdrop');
    case 'calculation': return t('exam.calculation');
    case 'truefalse': return t('exam.truefalse');
    default: return t('exam.selectBest');
  }
}

export function isCorrectOption(question: Question, opt: string): boolean {
  const correctIndexes = getCorrectIndexes(question);
  const optIndex = getOptionIndex(question, opt);
  return correctIndexes.includes(optIndex);
}

export function isCorrectAnswer(question: Question, answer: AnswerValue): boolean {
  if (isAnswerEmpty(answer)) return false;
  const type = normalizeType(question.type);

  // Matching
  if (type === 'matching') {
    const mapping = answer as unknown as Record<string, string>;
    const itemsA = question.match_items_a_en || [];
    const expected = parseMatchingCorrect(question.correct_answer);
    if (Object.keys(mapping).length !== itemsA.length) return false;
    return itemsA.every((item) => mapping[item] === expected[item]);
  }

  // Sequence
  if (type === 'sequence') {
    const order = answer as unknown as string[];
    const correct = getCorrectSequence(question);
    if (order.length !== correct.length) return false;
    return order.every((item, idx) => item === correct[idx]);
  }

  // Drag-and-drop / categorization
  if (type === 'dragdrop') {
    const mapping = answer as unknown as Record<string, string[]>;
    const expected = parseDragDropCorrect(question.correct_answer);
    const categories = question.categories_en || [];
    return categories.every((cat) => {
      const actual = (mapping[cat] || []).slice().sort();
      const exp = (expected[cat] || []).slice().sort();
      return actual.length === exp.length && actual.every((it, i) => it === exp[i]);
    });
  }

  // Calculation
  if (type === 'calculation') {
    const correctValue = Number(question.correct_answer);
    const tolerance = question.calculation_tolerance ?? 0.01;
    return Math.abs(Number(answer) - correctValue) <= tolerance;
  }

  // Single / multiple / hotspot / truefalse
  const correctIndexes = getCorrectIndexes(question);
  if (Array.isArray(answer)) {
    if (answer.length !== correctIndexes.length) return false;
    const selectedIndexes = answer.map((item) => getOptionIndex(question, item)).sort();
    const sortedCorrect = [...correctIndexes].sort();
    return selectedIndexes.every((idx, i) => idx === sortedCorrect[i]);
  }
  if (typeof answer === 'string') {
    const answerIndex = getOptionIndex(question, answer);
    return correctIndexes.length === 1 && correctIndexes[0] === answerIndex;
  }
  return false;
}

export function isAnswerEmpty(answer: AnswerValue | undefined): boolean {
  if (answer === null || answer === undefined || answer === '') return true;
  if (Array.isArray(answer) && answer.length === 0) return true;
  if (typeof answer === 'object' && Object.keys(answer).length === 0) return true;
  return false;
}

export function canRenderQuestion(question: Question): boolean {
  const type = normalizeType(question.type);
  if (type === 'single' || type === 'multiple' || type === 'hotspot' || type === 'truefalse') {
    return Array.isArray(question.options_en) && question.options_en.length > 0;
  }
  if (type === 'matching') {
    return (question.match_items_a_en?.length || 0) > 0 && (question.match_items_b_en?.length || 0) > 0;
  }
  if (type === 'sequence') {
    return (question.sequence_items_en?.length || 0) > 0;
  }
  if (type === 'dragdrop') {
    return (question.categories_en?.length || 0) > 0 && (question.dragdrop_items_en?.length || 0) > 0;
  }
  if (type === 'calculation') {
    return question.correct_answer !== undefined && question.correct_answer !== null;
  }
  return false;
}

// Internal parsing helpers
function parseMatchingCorrect(correctAnswer: AnswerValue): Record<string, string> {
  const result: Record<string, string> = {};
  if (typeof correctAnswer === 'string') {
    correctAnswer.split('|').forEach((pair) => {
      const [a, b] = pair.split('->').map((s) => s.trim());
      if (a && b) result[a] = b;
    });
  } else if (typeof correctAnswer === 'object' && correctAnswer !== null && !Array.isArray(correctAnswer)) {
    Object.assign(result, correctAnswer);
  }
  return result;
}

function getCorrectMatch(question: Question, itemA: string): string {
  const expected = parseMatchingCorrect(question.correct_answer);
  return expected[itemA] || '';
}

function getCorrectSequence(question: Question): string[] {
  if (typeof question.correct_answer === 'string') {
    return question.correct_answer.split('|').map((s) => s.trim()).filter(Boolean);
  }
  if (Array.isArray(question.correct_answer)) {
    return question.correct_answer.map((s) => String(s));
  }
  return [];
}

function parseDragDropCorrect(correctAnswer: AnswerValue): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (typeof correctAnswer === 'string') {
    correctAnswer.split('|').forEach((group) => {
      const [cat, items] = group.split(':');
      if (cat && items) result[cat.trim()] = items.split(',').map((s) => s.trim()).filter(Boolean);
    });
  } else if (typeof correctAnswer === 'object' && correctAnswer !== null && !Array.isArray(correctAnswer)) {
    Object.entries(correctAnswer).forEach(([cat, items]) => {
      result[cat] = Array.isArray(items) ? items.map((s) => String(s)) : [String(items)];
    });
  }
  return result;
}

function getCorrectCategory(question: Question, item: string): string {
  const expected = parseDragDropCorrect(question.correct_answer);
  for (const [cat, items] of Object.entries(expected)) {
    if (items.includes(item)) return cat;
  }
  return '';
}
